// @ts-nocheck
/**
 * PDP Detail v2 — used by section-pdp-detail-v2.liquid (accessory PDPs).
 *
 * Standalone copy of pdp-scrambler-detail.js. Class, bootstrap function and
 * the data-testid hook are all renamed so this never collides with the
 * Scrambler section's script. Edits here do NOT affect any other PDP.
 */

class PDPDetailV2 {
  constructor(sectionElement) {
    this.section = sectionElement;
    this.sectionId = sectionElement.dataset.sectionId || '';

    this.splideElement = this.section.querySelector('.scrambler-main-splide');
    this.galleryMain = this.section.querySelector('.scrambler-gallery-main');
    this.galleryDotsContainer = this.section.querySelector('.scrambler-gallery-dots');
    this.imageGrid = this.section.querySelector('.scrambler-image-grid');
    this.variantPicker = this.section.querySelector('variant-picker');
    this.variantInput = this.section.querySelector('[name="id"]');
    this.productForm = this.section.querySelector('.product-form');

    this.variants = this.getVariantsData();
    this.currentVariant = this.getCurrentVariant();

    // Thumbnail fill mode (section setting): 'auto' | 'on' | 'off'.
    // - auto: JPG thumbnails fill the bordered square, PNG stays as-is
    // - on: all thumbnails fill
    // - off: no thumbnails fill
    // Per-image Alt-text override (#fill / #nofill) wins over this.
    this.thumbnailFillMode = this.section.dataset.thumbnailFillMode || 'auto';

    // Variant -> color-specific gallery images (native image + gallery_images
    // metafield). Variants without any are absent → they fall back to the
    // full authored product gallery.
    this.variantMediaMap = this.getVariantMediaMap();
    // Authored main-gallery slide HTML, captured once for the fallback path.
    this.authoredSlidesHtml = [];

    // Personalization state
    this.personalizationConfirmed = false;

    this.splide = null;
    this.init();
  }

  getVariantMediaMap() {
    var el = document.getElementById('product-variant-media-' + this.sectionId);
    if (el) {
      try { return JSON.parse(el.textContent) || {}; } catch (e) {}
    }
    return {};
  }

  getVariantsData() {
    var el = document.getElementById('product-variants-' + this.sectionId);
    if (el) {
      try { return JSON.parse(el.textContent); } catch (e) {}
    }
    return [];
  }

  getCurrentVariant() {
    if (this.variantPicker) {
      var json = this.variantPicker.querySelector('script[type="application/json"]');
      if (json) {
        try { return JSON.parse(json.textContent); } catch (e) {}
      }
    }
    return null;
  }

  init() {
    // Capture the authored main-gallery slides once (for the fallback path
    // when a variant has no color-specific images).
    if (this.splideElement && !this.authoredSlidesHtml.length) {
      var authoredList = this.splideElement.querySelector('.splide__list');
      if (authoredList) {
        this.authoredSlidesHtml = Array.prototype.map.call(
          authoredList.children,
          function (slide) { return slide.outerHTML; }
        );
      }
    }

    // Gallery
    if (this.splideElement && window.Splide) {
      // Read arrow visibility from data-attrs set by the section schema.
      // Defaults: arrows OFF on desktop AND mobile (current behaviour).
      var showArrows = this.splideElement.dataset.showArrows === 'true';
      var hideArrowsMobile = this.splideElement.dataset.hideArrowsMobile === 'true';
      this.splide = new window.Splide(this.splideElement, {
        type: 'fade',
        perPage: 1,
        arrows: showArrows,
        pagination: false,
        drag: true,
        speed: 500,
        rewind: true,
        breakpoints: hideArrowsMobile ? {
          768: { arrows: false },
        } : undefined,
      });
      this.splide.mount();
      this.createGalleryDots();
      this.attachImageGridListeners();

      // Build the color-specific gallery for the initial variant (or leave the
      // authored gallery as-is when the variant has no metafield images).
      if (this.currentVariant) this.applyVariantMedia(this.currentVariant);
    }

    if (!window.Splide && this.splideElement) {
      var self = this;
      window.addEventListener('load', function() { self.init(); });
      return;
    }

    this.attachVariantPickerListeners();
    this.attachAddOnListeners();
    this.attachPersonalizationListeners();
    this.attachFormSubmitHandler();
    this.applySwatchStates();
  }

  // ---- VARIANT PICKER ----

  attachVariantPickerListeners() {
    if (!this.variantPicker) return;

    this.variantPicker.addEventListener('variant-update', (event) => {
      var variant = event.detail?.variant;
      if (variant) this.onVariantChange(variant);
      this.applySwatchStates();
    });

    this.variantPicker.addEventListener('change', (event) => {
      if (!event.target.matches('input[type="radio"]')) return;
      if (this._ignoreChange) return;

      var swatchGroup = event.target.dataset.swatchGroup;
      if (swatchGroup) this.selectGroupOption(swatchGroup);

      this.applySwatchStates();
      this.updateAddToCartForAvailability();

      // Update price when variant changes via radio click
      var variant = this.findVariantFromInputs();
      if (variant) this.onVariantChange(variant);
    });
  }

  applySwatchStates() {
    if (!this.variantPicker) return;

    this.variantPicker.querySelectorAll('.color-swatch-label').forEach((label) => {
      var input = label.querySelector('input[type="radio"]');
      if (input) label.classList.toggle('selected', input.checked);
    });

    this.variantPicker.querySelectorAll('.option-button-label').forEach((label) => {
      var input = label.querySelector('input[type="radio"]');
      if (input) label.classList.toggle('active', input.checked);
    });

    if (!this.variants || this.variants.length === 0) return;

    var fieldsets = this.variantPicker.querySelectorAll('[data-option-position]');
    if (fieldsets.length === 0) return;

    var selectedOptions = this.getSelectedOptions();
    var groupOptionIndex = -1;
    var firstGrouped = this.variantPicker.querySelector('input[data-swatch-group]');
    if (firstGrouped) {
      var groupVal = firstGrouped.dataset.swatchGroup;
      fieldsets.forEach((fs, idx) => {
        if (groupOptionIndex >= 0) return;
        var match = fs.querySelector('input[type="radio"][value="' + CSS.escape(groupVal) + '"]');
        if (match && !match.dataset.swatchGroup) groupOptionIndex = idx;
      });
    }

    var colorFieldsetIndex = firstGrouped
      ? parseInt(firstGrouped.closest('[data-option-position]')?.dataset.optionPosition || '1') - 1
      : -1;

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        var swatchGroup = input.dataset.swatchGroup;
        var isAvailable;

        if (swatchGroup) {
          isAvailable = true;
        } else if (groupOptionIndex >= 0 && (fieldsetIndex === groupOptionIndex || fieldsetIndex === colorFieldsetIndex)) {
          isAvailable = this.variants.some((v) => v.available && v.options[fieldsetIndex] === input.value);
        } else if (groupOptionIndex >= 0) {
          var opts = [...selectedOptions];
          opts[colorFieldsetIndex] = null;
          isAvailable = this.isOptionValueAvailable(input.value, fieldsetIndex, opts);
        } else {
          isAvailable = this.isOptionValueAvailable(input.value, fieldsetIndex, selectedOptions);
        }

        var label = input.closest('label');
        if (label) {
          label.classList.toggle('unavailable', !isAvailable);
          if (isAvailable) label.classList.remove('disabled');
        }
        if (swatchGroup && input.disabled) input.disabled = false;
      });
    });
  }

  selectGroupOption(groupValue) {
    if (!this.variantPicker) return;
    var allInputs = this.variantPicker.querySelectorAll('input[type="radio"]');
    for (var input of allInputs) {
      if (input.dataset.swatchGroup) continue;
      if (input.value !== groupValue || input.checked) continue;
      input.checked = true;
      var label = input.closest('label');
      if (label) {
        label.closest('fieldset')?.querySelectorAll('.option-button-label').forEach(l => l.classList.remove('active'));
        label.classList.add('active');
      }
      this._ignoreChange = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this._ignoreChange = false;
      break;
    }
  }

  updateAddToCartForAvailability() {
    var btn = this.section.querySelector('.product-add-to-cart');
    if (!btn) return;
    var variant = this.findVariantFromInputs();
    if (variant && this.variantInput) this.variantInput.value = variant.id;
    if (variant && variant.available) {
      btn.disabled = false;
      btn.textContent = btn.dataset.addText || 'Add to Cart';
    } else if (variant && !variant.available) {
      btn.disabled = true;
      btn.textContent = btn.dataset.soldOutText || 'Sold Out';
    } else {
      btn.disabled = true;
      btn.textContent = 'Unavailable';
    }
  }

  getSelectedOptions() {
    var opts = [];
    this.variantPicker.querySelectorAll('[data-option-position]').forEach((fs) => {
      var checked = fs.querySelector('input[type="radio"]:checked');
      opts.push(checked ? checked.value : null);
    });
    return opts;
  }

  isOptionValueAvailable(optionValue, optionIndex, selectedOptions) {
    return this.variants.some((variant) => {
      if (variant.options[optionIndex] !== optionValue) return false;
      if (!variant.available) return false;
      for (var i = 0; i < selectedOptions.length; i++) {
        if (i === optionIndex) continue;
        if (selectedOptions[i] && variant.options[i] !== selectedOptions[i]) return false;
      }
      return true;
    });
  }

  findVariantFromInputs() {
    // Single-variant products ("Default Title") render no <variant-picker> —
    // the section guards it behind `unless product.has_only_default_variant`.
    // Without this fallback we return null and the button locks to "Unavailable".
    if (!this.variantPicker) {
      return this.variants.length === 1 ? this.variants[0] : null;
    }
    var selectedOptions = this.getSelectedOptions();
    if (selectedOptions.length === 0 || selectedOptions.every(v => v === null)) return null;
    return this.variants.find((variant) => {
      return variant.options.every((option, index) => {
        return selectedOptions[index] === null || option === selectedOptions[index];
      });
    }) || null;
  }

  onVariantChange(variant) {
    if (this.variantInput) this.variantInput.value = variant.id;
    this.updatePriceDisplay(variant);
    this.updateAddToCartButton(variant);
    this.updateURL(variant);
    this.applyVariantMedia(variant);
    this.updateShipDate(variant);
  }

  updateShipDate(variant) {
    if (!variant) return;
    var shipEl = this.section.querySelector('[data-ship-date]');
    var mapEl = this.section.querySelector('[data-ship-date-map]');
    if (!shipEl || !mapEl) return;
    var map;
    try { map = JSON.parse(mapEl.textContent); } catch (e) { return; }
    var value = map[String(variant.id)];
    if (value && String(value).trim() !== '') {
      shipEl.textContent = value;
      shipEl.style.display = '';
    } else {
      shipEl.textContent = '';
      shipEl.style.display = 'none';
    }
  }

  updatePriceDisplay(variant) {
    this.currentVariant = variant;

    // Update ALL pricing containers (mobile header + main)
    var containers = this.section.querySelectorAll('.product-pricing');
    containers.forEach(function(container) {
      container.dataset.basePrice = variant.price;

      var salePrice = container.querySelector('.price--sale, .price--current');
      var originalPrice = container.querySelector('.price--original:not(.price--with-upgrades)');
      var badge = container.querySelector('.product-discount-badge:not(.product-savings-badge)');

      if (salePrice) {
        salePrice.textContent = this.formatMoney(variant.price);
        salePrice.dataset.price = variant.price;
      }

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        if (originalPrice) {
          originalPrice.textContent = this.formatMoney(variant.compare_at_price);
          originalPrice.dataset.comparePrice = variant.compare_at_price;
          originalPrice.style.display = '';
        }
        if (badge) {
          badge.textContent = 'Save ' + this.formatMoney(variant.compare_at_price - variant.price);
          badge.style.display = '';
        }
      } else {
        if (originalPrice) originalPrice.style.display = 'none';
        if (badge) badge.style.display = 'none';
      }
    }.bind(this));

    this.updatePriceWithUpgrades();
  }

  updateAddToCartButton(variant) {
    var btn = this.section.querySelector('.product-add-to-cart');
    if (!btn) return;
    if (variant.available) {
      btn.disabled = false;
      btn.textContent = btn.dataset.addText || 'Add to Cart';
    } else {
      btn.disabled = true;
      btn.textContent = btn.dataset.soldOutText || 'Sold Out';
    }
  }

  updateURL(variant) {
    if (!history.replaceState || !variant) return;
    var url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({ variant: variant.id }, '', url.toString());
  }

  formatMoney(cents) {
    const n = Number(cents);
    if (!isFinite(n)) return '';
    return '$' + (n / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  // ---- GALLERY DOTS ----

  createGalleryDots() {
    if (!this.splide || !this.galleryDotsContainer) return;
    var total = this.splide.length;
    var splide = this.splide;
    var container = this.galleryDotsContainer;
    var self = this;

    // This runs again on every variant switch — drop the previous namespaced
    // 'moved' handler first so listeners don't stack up across rebuilds.
    splide.off('moved.dots');

    // Render dots inside an inner scrollable track so the outer container
    // can act as a fixed-width window showing only N dots at a time.
    container.innerHTML = '';
    var track = document.createElement('div');
    track.className = 'scrambler-gallery-dots-track';
    container.appendChild(track);

    var slideImgs = this.splideElement.querySelectorAll('.splide__slide img');

    for (var i = 0; i < total; i++) {
      var dot = document.createElement('button');
      dot.className = 'scrambler-gallery-dot' + (i === 0 ? ' is-active' : '');
      dot.type = 'button';
      dot.dataset.index = i;

      var slideImg = slideImgs[i];
      if (slideImg) {
        var thumb = document.createElement('img');
        thumb.className = 'scrambler-gallery-dot__img';
        var thumb1x = slideImg.getAttribute('data-thumb-url') || slideImg.src;
        var thumb2x = slideImg.getAttribute('data-thumb-url-2x');
        // Fill the bordered square (photos) vs. default contain (cut-outs),
        // decided by the section mode + per-image Alt-text override.
        if (this.shouldFillThumb(thumb1x, slideImg.getAttribute('alt'))) {
          thumb.className += ' scrambler-gallery-dot__img--jpg';
        }
        thumb.src = thumb1x;
        if (thumb2x) {
          thumb.srcset = thumb1x + ' 1x, ' + thumb2x + ' 2x';
        }
        thumb.alt = '';
        thumb.loading = 'lazy';
        thumb.decoding = 'async';
        dot.appendChild(thumb);
      }

      dot.addEventListener('click', function(e) {
        // Suppress click that follows a drag.
        if (track.dataset.dragMoved === '1') return;
        splide.go(parseInt(this.dataset.index));
      });
      track.appendChild(dot);
    }

    // Drag-to-scroll on the dot strip. Only kicks in after the pointer
    // moves past a 5px threshold; below that, the user is tapping a dot
    // and the inner button's click handler runs normally.
    // We do NOT use setPointerCapture — it would redirect events to the
    // track and prevent clicks on the inner buttons from firing.
    (function attachDragScroll() {
      var isDown = false;
      var startX = 0;
      var startY = 0;
      var startScroll = 0;
      var dragging = false;
      var DRAG_THRESHOLD = 5;

      track.addEventListener('pointerdown', function(e) {
        // Only start tracking on primary button (mouse) or any touch/pen.
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        isDown = true;
        dragging = false;
        startX = e.clientX;
        startY = e.clientY;
        startScroll = track.scrollLeft;
        track.dataset.dragMoved = '0';
      });

      track.addEventListener('pointermove', function(e) {
        if (!isDown) return;
        var dx = e.clientX - startX;
        var dy = e.clientY - startY;
        if (!dragging && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
          // Past the threshold — start dragging. Only treat as a horizontal
          // drag if X movement dominates (so vertical page scrolls aren't hijacked).
          if (Math.abs(dx) > Math.abs(dy)) {
            dragging = true;
            track.dataset.dragMoved = '1';
          } else {
            isDown = false;
            return;
          }
        }
        if (dragging) {
          e.preventDefault();
          track.scrollLeft = startScroll - dx;
        }
      });

      function endDrag() {
        if (!isDown) return;
        isDown = false;
        // Clear the flag on the next tick — after the synthetic click fires.
        var wasDragging = dragging;
        dragging = false;
        if (wasDragging) {
          setTimeout(function() { track.dataset.dragMoved = '0'; }, 0);
        } else {
          track.dataset.dragMoved = '0';
        }
      }
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      window.addEventListener('pointerup', endDrag);
    })();

    function scrollActiveIntoView(idx) {
      var dot = track.children[idx];
      if (!dot) return;
      // Read the visible-count from --dots-visible (set by the section
      // schema). Always use this — never measure track.clientWidth during
      // a variant switch (reflow can give a stale value that snaps the
      // active dot to the right edge and clips the neighbour as a sliver).
      var dotWidth = dot.offsetWidth;
      var styles = window.getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap || 0) || 0;
      var stride = dotWidth + gap;

      var containerStyles = window.getComputedStyle(container);
      var visibleCount = parseInt(
        containerStyles.getPropertyValue('--dots-visible'),
        10
      );
      if (!visibleCount || isNaN(visibleCount) || visibleCount < 1) {
        visibleCount = 4; // schema default
      }

      var totalDots = track.children.length;
      var maxStart = Math.max(0, totalDots - visibleCount);
      var desiredStart = idx - Math.floor((visibleCount - 1) / 2);
      var start = Math.max(0, Math.min(desiredStart, maxStart));
      track.scrollTo({ left: start * stride, behavior: 'smooth' });
    }

    splide.on('moved.dots', function(newIndex) {
      track.querySelectorAll('.scrambler-gallery-dot').forEach(function(d, idx) {
        d.classList.toggle('is-active', idx === newIndex);
      });
      scrollActiveIntoView(newIndex);
    });

    // Swap the JPG frame state at the START of the move (using the destination
    // index) so the frame is already sized correctly as the new image fades in.
    // Doing this on 'moved' (end) makes the image appear to grow mid-fade.
    splide.off('move.frame');
    splide.on('move.frame', function(newIndex) {
      self.syncGalleryFrameForActiveSlide(newIndex);
    });

    // Set the initial frame state for whatever slide is active now.
    self.syncGalleryFrameForActiveSlide();
  }

  // ---- LIFESTYLE LIGHTBOX ----
  // The bottom grid is now a separate "lifestyle photos" gallery (sourced
  // from product.metafields.custom.lifestyle_images). Clicking a thumbnail
  // opens it in a full-screen lightbox with prev / next / close + keyboard.
  attachImageGridListeners() {
    if (!this.imageGrid) return;
    var self = this;
    var thumbs = Array.from(this.imageGrid.querySelectorAll('.scrambler-grid-thumb'));
    if (!thumbs.length) return;

    var lightbox = this.section.querySelector('[data-lightbox]');
    if (!lightbox) return;

    var imgEl = lightbox.querySelector('[data-lightbox-image]');
    var counter = lightbox.querySelector('[data-lightbox-counter]');
    var closeBtn = lightbox.querySelector('[data-lightbox-close]');
    var prevBtn = lightbox.querySelector('[data-lightbox-prev]');
    var nextBtn = lightbox.querySelector('[data-lightbox-next]');

    var slides = thumbs.map(function(thumb) {
      return {
        src: thumb.getAttribute('data-lightbox-src') || thumb.querySelector('img')?.src,
        srcset: thumb.getAttribute('data-lightbox-srcset') || '',
        alt: thumb.getAttribute('data-lightbox-alt') || ''
      };
    });

    var currentIndex = 0;

    function render(i) {
      currentIndex = (i + slides.length) % slides.length;
      var slide = slides[currentIndex];
      if (imgEl) {
        imgEl.src = slide.src;
        if (slide.srcset) imgEl.setAttribute('srcset', slide.srcset);
        else imgEl.removeAttribute('srcset');
        imgEl.alt = slide.alt;
      }
      if (counter) counter.textContent = (currentIndex + 1) + ' / ' + slides.length;
    }

    function open(i) {
      render(i || 0);
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', onKey);
    }

    function close() {
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    }

    function onKey(e) {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') render(currentIndex - 1);
      else if (e.key === 'ArrowRight') render(currentIndex + 1);
    }

    thumbs.forEach(function(thumb, i) {
      thumb.addEventListener('click', function(e) {
        e.preventDefault();
        open(i);
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', close);
    if (prevBtn) prevBtn.addEventListener('click', function() { render(currentIndex - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function() { render(currentIndex + 1); });

    // Click outside the figure (on the dark backdrop) closes the lightbox
    lightbox.addEventListener('click', function(e) {
      if (e.target === lightbox) close();
    });
  }

  // Go to the carousel slide that matches a variant's featured image
  goToVariantImage(variant) {
    if (!this.splide || !variant || !variant.featured_media) return;
    var mediaId = variant.featured_media.id;
    var slides = this.section.querySelectorAll('.scrambler-main-splide .splide__slide');
    for (var i = 0; i < slides.length; i++) {
      var img = slides[i].querySelector('img');
      if (img && img.dataset.mediaId && parseInt(img.dataset.mediaId) === mediaId) {
        this.splide.go(i);
        return;
      }
    }
  }

  // Escape a string for safe use inside an HTML attribute.
  escapeAttribute(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // Detect a JPG/JPEG from an image URL. Shopify CDN urls put the extension
  // before the query string (…/file.jpg?v=123&width=1200), so we test the
  // path portion only.
  isJpegUrl(url) {
    var path = String(url || '').split('?')[0];
    return /\.jpe?g$/i.test(path);
  }

  // Toggle the JPG frame treatment (padding: 0) based on the currently active
  // main slide. Called on init, on carousel move, and after a rebuild. Each JPG
  // featured image removes the frame padding; PNG cut-outs keep it.
  syncGalleryFrameForActiveSlide(index) {
    if (!this.galleryMain || !this.splide) return;
    var slides = this.splideElement.querySelectorAll('.splide__slide');
    var targetIndex = (typeof index === 'number') ? index : (this.splide.index || 0);
    var targetSlide = slides[targetIndex];
    var img = targetSlide ? targetSlide.querySelector('img') : null;
    var src = img ? (img.getAttribute('src') || '') : '';
    this.galleryMain.classList.toggle('is-jpg-active', this.isJpegUrl(src));
  }

  // Decide whether a thumbnail should "fill" the bordered square.
  // PNG cut-outs are NEVER disturbed except by an explicit #fill Alt tag.
  // Priority:
  //   1. Per-image Alt-text override: #nofill forces off, #fill forces on
  //   2. Section mode 'off' → never fill
  //   3. Per-variant flag (custom.gallery_images_fill) checked → fill this
  //      variant's JPG thumbnails only (PNGs stay as-is)
  //   4. Section mode 'on' → fill all
  //   5. 'auto' → only JPG photos (PNG cut-outs stay as-is)
  shouldFillThumb(url, alt) {
    var altText = String(alt || '').toLowerCase();
    if (altText.indexOf('#nofill') !== -1) return false;
    if (altText.indexOf('#fill') !== -1) return true;
    if (this.thumbnailFillMode === 'off') return false;
    if (this._currentVariantFill === true) return this.isJpegUrl(url);
    if (this.thumbnailFillMode === 'on') return true;
    return this.isJpegUrl(url); // 'auto'
  }

  // Build a main-gallery slide (image) from a variant image object. The
  // data-thumb-url attributes let createGalleryDots() render matching dots.
  buildVariantSlideHtml(image) {
    var src = this.escapeAttribute(image.src);
    var srcset = image.srcset ? ' srcset="' + this.escapeAttribute(image.srcset) + '"' : '';
    var alt = this.escapeAttribute(image.alt || '');
    var thumb = this.escapeAttribute(image.thumb || image.src);
    var thumb2xAttr = image.thumb2x ? ' data-thumb-url-2x="' + this.escapeAttribute(image.thumb2x) + '"' : '';
    return '<li class="splide__slide">' +
      '<img src="' + src + '"' + srcset + ' alt="' + alt + '" class="scrambler-gallery-image" loading="lazy"' +
      ' data-thumb-url="' + thumb + '"' + thumb2xAttr + '></li>';
  }

  // Update the main gallery for a variant.
  //
  // - Variant HAS gallery_images metafield: rebuild the gallery from those
  //   images (native variant image first, then the metafield images).
  // - Variant has NO metafield images: keep the full authored product gallery
  //   and just scroll it to this variant's image (original behaviour, unchanged).
  applyVariantMedia(variant) {
    if (!this.splide) return;
    var variantId = variant && variant.id != null ? variant.id : variant;
    var entry = this.variantMediaMap[String(variantId)];
    var variantImages = entry && entry.images ? entry.images : null;
    var hasMetafieldGallery = !!(variantImages && variantImages.length);

    if (!hasMetafieldGallery) {
      // Fallback: full authored product gallery. Its thumbnails follow the
      // section fill mode (no per-variant flag applies here).
      this._currentVariantFill = null;
      if (this._mediaStateKey && this._mediaStateKey !== '__fallback__') {
        this._mediaStateKey = '__fallback__';
        if (this.authoredSlidesHtml.length) {
          this.splide.remove(function () { return true; });
          this.splide.add(this.authoredSlidesHtml);
          this.createGalleryDots();
        }
      } else {
        this._mediaStateKey = '__fallback__';
      }
      this.goToVariantImage(variant);
      return;
    }

    // Per-variant thumbnail fill flag (custom.gallery_fill_jpg).
    this._currentVariantFill = !!entry.fill;

    // Variant-specific gallery from metafield images.
    var stateKey = variantImages.map(function (image) { return image.src; }).join('|');
    if (stateKey === this._mediaStateKey) return;
    this._mediaStateKey = stateKey;

    var slidesHtml = variantImages.map(this.buildVariantSlideHtml.bind(this));
    if (!slidesHtml.length) return;

    // Rebuild via Splide's remove/add so internal slide state stays correct.
    this.splide.remove(function () { return true; });
    this.splide.add(slidesHtml);

    // Rebuild the thumbnail dots to match the new slide set, reset to first.
    this.createGalleryDots();
    this.splide.go(0);
  }

  // ---- ADD-ONS ----

  attachAddOnListeners() {
    this.section.querySelectorAll('.free-item-checkbox').forEach((cb) => {
      cb.addEventListener('change', (e) => {
        var label = e.target.closest('.pdp-scrambler-addon-item');
        if (label) label.classList.toggle('is-checked', e.target.checked);
      });
    });

    this.section.querySelectorAll('.upgrade-item-checkbox').forEach((cb) => {
      cb.addEventListener('change', (e) => {
        var label = e.target.closest('.pdp-scrambler-addon-item');
        if (label) label.classList.toggle('is-checked', e.target.checked);
        this.updatePriceWithUpgrades();
      });
    });

    this.updatePriceWithUpgrades();
  }

  updatePriceWithUpgrades() {
    var container = this.section.querySelector('.product-information .product-pricing');
    if (!container) return;
    var hasSalePrice = container.querySelector('.price--sale') !== null;
    if (hasSalePrice) return;

    var basePrice = parseInt(container.dataset.basePrice) || (this.currentVariant?.price) || 0;
    var upgradeTotal = 0;
    this.section.querySelectorAll('.upgrade-item-checkbox:checked').forEach((cb) => {
      upgradeTotal += parseInt(cb.dataset.itemPriceValue) || 0;
    });

    var currentPrice = container.querySelector('.price--current');
    var upgradeOriginal = container.querySelector('.price--with-upgrades');
    var savingsBadge = container.querySelector('.product-savings-badge');
    if (!currentPrice) return;

    if (upgradeTotal > 0) {
      currentPrice.textContent = this.formatMoney(basePrice + upgradeTotal);
      if (upgradeOriginal) { upgradeOriginal.textContent = this.formatMoney(basePrice); upgradeOriginal.style.display = ''; }
      if (savingsBadge) { savingsBadge.textContent = '+' + this.formatMoney(upgradeTotal) + ' in upgrades'; savingsBadge.style.display = ''; }
    } else {
      currentPrice.textContent = this.formatMoney(basePrice);
      if (upgradeOriginal) upgradeOriginal.style.display = 'none';
      if (savingsBadge) savingsBadge.style.display = 'none';
    }
  }

  // ---- PERSONALIZATION ----

  attachPersonalizationListeners() {
    var section = this.section;
    var radio = section.querySelector('.pdp-personalization-radio');
    var fields = section.querySelector('.pdp-personalization-fields');
    var textInput = section.querySelector('.pdp-personalization-input, .pdp-personalization-v2__input, [data-personalization-name]');
    var charCount = section.querySelector('.pdp-personalization-char-count');
    var termsCheckbox = section.querySelector('.pdp-personalization-terms-checkbox');
    var termsLink = section.querySelector('.pdp-personalization-terms-link');
    var modalOverlay = section.querySelector('.pdp-terms-modal-overlay');
    var modalAcceptBtn = section.querySelector('.pdp-terms-modal-accept');
    var modalCancelBtn = section.querySelector('.pdp-terms-modal-cancel');
    var modalCloseBtn = section.querySelector('.pdp-terms-modal-close');
    var modalAcceptCheckbox = section.querySelector('.pdp-terms-modal-accept-checkbox');
    var self = this;

    if (!radio || !fields) return;

    // Fields are always visible (per Figma)
    fields.classList.add('is-visible');

    // Radio can only be checked when terms + letter fill are both set
    var styleSelect = section.querySelector('.pdp-personalization-select[data-field="letter-style"]');

    // ---- Custom dropdown wiring (replaces the native <select> with a styled
    // listbox so we can paint the yellow-green hover highlight) ----
    var customSelect = section.querySelector('.pdp-custom-select');
    if (customSelect && styleSelect) {
      var toggle = customSelect.querySelector('.pdp-custom-select__toggle');
      var label = customSelect.querySelector('.pdp-custom-select__label');
      var list = customSelect.querySelector('.pdp-custom-select__list');
      var options = list ? Array.from(list.querySelectorAll('.pdp-custom-select__option')) : [];
      var placeholderText = label ? (label.getAttribute('data-placeholder') || label.textContent) : '';

      function closeCustom() {
        customSelect.classList.remove('is-open');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      }
      function openCustom() {
        customSelect.classList.add('is-open');
        if (toggle) toggle.setAttribute('aria-expanded', 'true');
      }
      function pickOption(opt) {
        if (!opt || opt.classList.contains('is-unavailable')) return;
        options.forEach(function(o) { o.classList.remove('is-selected'); });
        opt.classList.add('is-selected');
        var val = opt.getAttribute('data-value') || '';
        styleSelect.value = val;
        if (label) label.textContent = opt.textContent;
        customSelect.classList.toggle('is-selected', !!val);
        // Trigger native change so existing listeners (radio state, pulse clear, etc.) fire
        styleSelect.dispatchEvent(new Event('change', { bubbles: true }));
        closeCustom();
      }

      if (toggle) {
        toggle.addEventListener('click', function(e) {
          e.preventDefault();
          if (customSelect.classList.contains('is-open')) closeCustom();
          else openCustom();
        });
      }

      options.forEach(function(opt) {
        opt.addEventListener('click', function(e) {
          e.stopPropagation();
          pickOption(opt);
        });
      });

      document.addEventListener('click', function(e) {
        if (!customSelect.contains(e.target)) closeCustom();
      });

      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape') closeCustom();
      });

      // Reflect initial state (if the native select has a value preset)
      if (styleSelect.value) {
        var initial = options.find(function(o) { return o.getAttribute('data-value') === styleSelect.value; });
        if (initial) pickOption(initial);
      }
    }

    function canCheckRadio() {
      var hasName = textInput && textInput.value.trim().length > 0;
      var hasStyle = styleSelect && styleSelect.value;
      var hasTerms = termsCheckbox && termsCheckbox.checked;
      return hasName && hasStyle && hasTerms;
    }

    function markEmptyFields() {
      // Flag empty required fields with a class so CSS can paint them grey
      if (textInput) {
        textInput.classList.toggle('needs-value', !textInput.value.trim());
      }
      if (styleSelect) {
        styleSelect.classList.toggle('needs-value', !styleSelect.value);
      }
      if (termsCheckbox) {
        var termsWrap = termsCheckbox.closest('.pdp-personalization-terms-wrap');
        if (termsWrap) termsWrap.classList.toggle('needs-value', !termsCheckbox.checked);
      }
    }

    function updateRadioState() {
      markEmptyFields();
      if (canCheckRadio()) {
        radio.disabled = false;
        radio.classList.remove('is-disabled');
      } else {
        radio.disabled = true;
        radio.checked = false;
        radio.classList.add('is-disabled');
      }
    }

    // Listen for changes on all required fields
    [textInput, styleSelect, termsCheckbox].forEach(function(el) {
      if (el) {
        el.addEventListener('input', updateRadioState);
        el.addEventListener('change', updateRadioState);
      }
    });

    // Initial state - radio disabled
    updateRadioState();

    var errorEl = section.querySelector('.pdp-personalization-error');

    function showError(msg) {
      if (errorEl) {
        errorEl.textContent = msg;
        errorEl.classList.add('is-visible');
      }
    }

    function hideError() {
      if (errorEl) {
        errorEl.classList.remove('is-visible');
      }
    }

    function pulseEmptyFields() {
      var targets = [];
      if (textInput && !textInput.value.trim()) targets.push(textInput);
      if (styleSelect && !styleSelect.value) targets.push(styleSelect);
      if (termsCheckbox && !termsCheckbox.checked) {
        var termsWrap = termsCheckbox.closest('.pdp-personalization-terms-wrap');
        if (termsWrap) targets.push(termsWrap);
      }
      targets.forEach(function(el) {
        el.classList.remove('is-pulsing');
        // Force reflow so the animation restarts on repeat clicks
        // eslint-disable-next-line no-unused-expressions
        el.offsetWidth;
        el.classList.add('is-pulsing');
      });
      setTimeout(function() {
        targets.forEach(function(el) { el.classList.remove('is-pulsing'); });
      }, 1200);
    }

    // When the radio is `disabled` the input swallows clicks silently.
    // Listen on the parent <label> so the pulse + error still trigger.
    var radioRow = radio.closest('.pdp-personalization-radio-row') || radio;
    radioRow.addEventListener('click', function(e) {
      if (!canCheckRadio()) {
        e.preventDefault();
        pulseEmptyFields();
        var errors = [];
        if (!textInput || !textInput.value.trim()) errors.push('Please enter a name');
        if (!styleSelect || !styleSelect.value) errors.push('please select a letter fill option');
        if (!termsCheckbox || !termsCheckbox.checked) errors.push('and accept the terms');
        showError(errors.join(', ') + '.');
      } else {
        hideError();
      }
    });

    // Hide error when all requirements are met
    [textInput, styleSelect, termsCheckbox].forEach(function(el) {
      if (el) {
        el.addEventListener('input', function() {
          if (canCheckRadio()) hideError();
        });
        el.addEventListener('change', function() {
          if (canCheckRadio()) hideError();
        });
      }
    });

    // Letter mask: allow only letters, numbers, and spaces in the name input
    if (textInput) {
      textInput.addEventListener('input', function(e) {
        var cleaned = textInput.value.replace(/[^A-Za-z0-9 ]/g, '');
        if (textInput.value !== cleaned) {
          textInput.value = cleaned;
        }
      });
    }

    // Description Read More / Show Less toggle
    var descWrap = section.querySelector('.pdp-personalization-description');
    var descToggle = section.querySelector('.pdp-personalization-description-toggle');
    if (descWrap && descToggle) {
      descToggle.addEventListener('click', function() {
        var expanded = descToggle.dataset.expanded === 'true';
        if (expanded) {
          descWrap.classList.add('is-collapsed');
          descToggle.textContent = 'Read More';
          descToggle.dataset.expanded = 'false';
        } else {
          descWrap.classList.remove('is-collapsed');
          descToggle.textContent = 'Show Less';
          descToggle.dataset.expanded = 'true';
        }
      });
    }

    // Character count
    if (textInput && charCount) {
      var maxLen = parseInt(textInput.getAttribute('maxlength')) || 12;
      textInput.addEventListener('input', function() {
        charCount.textContent = textInput.value.length + '/' + maxLen;
      });
    }

    // Terms link opens modal
    if (termsLink && modalOverlay) {
      termsLink.addEventListener('click', function(e) {
        e.preventDefault();
        modalOverlay.classList.add('is-open');
        if (modalAcceptCheckbox) modalAcceptCheckbox.checked = false;
      });
    }

    // Terms checkbox opens modal
    if (termsCheckbox && modalOverlay) {
      termsCheckbox.addEventListener('change', function() {
        if (termsCheckbox.checked && !self.personalizationConfirmed) {
          termsCheckbox.checked = false;
          modalOverlay.classList.add('is-open');
          if (modalAcceptCheckbox) modalAcceptCheckbox.checked = false;
        }
      });
    }

    // Modal accept
    if (modalAcceptBtn && modalOverlay) {
      modalAcceptBtn.addEventListener('click', function() {
        if (modalAcceptCheckbox && !modalAcceptCheckbox.checked) return;
        self.personalizationConfirmed = true;
        if (termsCheckbox) termsCheckbox.checked = true;
        modalOverlay.classList.remove('is-open');
      });
    }

    // Modal cancel
    if (modalCancelBtn && modalOverlay) {
      modalCancelBtn.addEventListener('click', function() {
        self.personalizationConfirmed = false;
        if (termsCheckbox) termsCheckbox.checked = false;
        modalOverlay.classList.remove('is-open');
      });
    }

    // Modal close X
    if (modalCloseBtn && modalOverlay) {
      modalCloseBtn.addEventListener('click', function() {
        modalOverlay.classList.remove('is-open');
      });
    }

    // Close modal on overlay click
    if (modalOverlay) {
      modalOverlay.addEventListener('click', function(e) {
        if (e.target === modalOverlay) {
          modalOverlay.classList.remove('is-open');
        }
      });
    }
  }

  getPersonalizationItem() {
    var radio = this.section.querySelector('.pdp-personalization-radio');
    if (!radio || !radio.checked) return null;

    var textInput = this.section.querySelector('.pdp-personalization-input, .pdp-personalization-v2__input, [data-personalization-name]');
    var styleSelect = this.section.querySelector('.pdp-personalization-select[data-field="letter-style"]');
    var nameplateVariantId = this.section.querySelector('[data-nameplate-variant-id]');

    if (!nameplateVariantId) return null;

    var name = textInput ? textInput.value.trim() : '';
    var letterStyle = styleSelect ? styleSelect.value : '';

    // Blank name = customer declined the nameplate ("leave box blank to
    // forego"). Never create a nameplate line item with nothing to engrave —
    // it would reach the warehouse as an unfulfillable blank plate.
    if (!name) return null;

    var properties = {
      'Name': name,
      '_note': nameplateVariantId.dataset.nameplateNote || 'Ships in individual box, within 3 weeks'
    };

    // v2 renders no letter-fill select. Only send the property when a value
    // exists — an empty one renders as a blank line at checkout and on the
    // packing slip.
    if (letterStyle) properties['Letter Fill'] = letterStyle;

    // If the style select has a data-variant-id on its selected option, use that instead
    var variantIdToUse = parseInt(nameplateVariantId.dataset.nameplateVariantId);
    if (styleSelect && styleSelect.selectedIndex >= 0) {
      var selectedOption = styleSelect.options[styleSelect.selectedIndex];
      if (selectedOption && selectedOption.dataset.variantId) {
        variantIdToUse = parseInt(selectedOption.dataset.variantId);
      }
    }

    return {
      id: variantIdToUse,
      quantity: 1,
      properties: properties
    };
  }

  validatePersonalization() {
    var radio = this.section.querySelector('.pdp-personalization-radio');
    if (!radio || !radio.checked) return true; // Not selected, no validation needed

    var errorEl = this.section.querySelector('.pdp-personalization-error');
    var textInput = this.section.querySelector('.pdp-personalization-input, .pdp-personalization-v2__input, [data-personalization-name]');
    var styleSelect = this.section.querySelector('.pdp-personalization-select[data-field="letter-style"]');
    var termsCheckbox = this.section.querySelector('.pdp-personalization-terms-checkbox');

    var errors = [];
    if (!textInput || !textInput.value.trim()) errors.push('Please enter a name.');
    if (styleSelect && !styleSelect.value) errors.push('Please select a letter fill option.');
    if (termsCheckbox && !termsCheckbox.checked) errors.push('Please accept the personalization terms.');

    if (errors.length > 0 && errorEl) {
      errorEl.textContent = errors[0];
      errorEl.classList.add('is-visible');
      return false;
    }

    if (errorEl) errorEl.classList.remove('is-visible');
    return true;
  }

  // ---- FORM SUBMIT ----

  attachFormSubmitHandler() {
    if (!this.productForm) return;

    this.productForm.addEventListener('submit', async (e) => {
      e.preventDefault();

      var addToCartBtn = this.section.querySelector('.product-add-to-cart');
      var currentVariant = this.findVariantFromInputs();

      if (!currentVariant || !currentVariant.available) {
        if (addToCartBtn) addToCartBtn.textContent = currentVariant ? 'Sold Out' : 'Unavailable';
        return;
      }

      // Validate personalization if selected
      if (!this.validatePersonalization()) return;

      if (addToCartBtn) {
        addToCartBtn.disabled = true;
        addToCartBtn.textContent = 'Adding...';
      }

      try {
        // Build items with main product first so it appears on top in the
        // cart drawer list ahead of upgrades / personalization.
        var itemsToAdd = [];
        // Parallel array of human-readable info for each itemsToAdd entry
        var itemsMeta = [];

        var mainProperties = { _is_main_product: 'true' };
        // Ship time for the order email is read DIRECTLY from the variant
        // metafield (custom.order_confirmation_ship_time) inside the Order
        // Confirmation notification template — notification Liquid supports
        // line.variant.metafields per line. So we intentionally do NOT attach
        // it as a line-item property here: a non-underscore property would show
        // at checkout, and an underscore one is hidden from the email too.
        // Reading the metafield live keeps it off checkout entirely.
        var checkedFreeItems = this.section.querySelectorAll('.free-item-checkbox:checked');
        checkedFreeItems.forEach((cb, i) => {
          var name = cb.dataset.itemName || '';
          // These _free_* properties only drive the nested "bundle row" display
          // under the bike in the cart drawer. Once the item has a linked_product
          // it is added as its OWN cart line, so drawing it here too would show
          // the same accessory twice. Keep the display rows only for items that
          // have no line item of their own.
          if (name && !parseInt(cb.dataset.variantId, 10)) {
            mainProperties['_free_' + (i + 1) + '_name'] = name;
            mainProperties['_free_' + (i + 1) + '_image'] = cb.dataset.itemImage || '';
            mainProperties['_free_' + (i + 1) + '_price'] = cb.dataset.itemPrice || '';
          }
          // Capture any per-item name input (e.g. Personalized Nameplate "Type Name Here")
          // so the typed value travels with the bike line and reaches the order even
          // when the free item itself is no longer pushed as a separate line item.
          var wrap = cb.closest('.product-add-ons-item-wrapper');
          var nameInput = wrap ? wrap.querySelector('.product-add-ons-name-input') : null;
          if (nameInput && nameInput.value.trim()) {
            // Fallback ONLY. When this free item has a linked_product it becomes
            // its own line item and carries `Name` itself; copying it here too
            // would print "Name: ..." on the bike line at checkout as well.
            if (!parseInt(cb.dataset.variantId, 10)) {
              mainProperties['Name'] = nameInput.value.trim();
            }
          }
        });
        itemsToAdd.push({
          id: parseInt(currentVariant.id),
          quantity: 1,
          properties: mainProperties
        });
        itemsMeta.push({
          source: 'main',
          name: (window.__pdpProductTitle || document.title || 'Main product'),
          variantId: currentVariant.id
        });

        // Push free items as their own line items so they reach checkout.
        // Only items with a linked variant ID (mapped to a $0 product) are added.
        checkedFreeItems.forEach((cb) => {
          var freeVariantId = parseInt(cb.dataset.variantId, 10);
          if (!freeVariantId) {
            // No `linked_product` on this free item's metaobject entry, so there
            // is no variant to add. The item then survives only as a _free_*
            // property on the bike line — it looks right in the cart drawer but
            // creates NOTHING to fulfil, which is what forced staff to raise a
            // manual draft order per nameplate. Make that visible instead of
            // failing silently.
            console.warn(
              '[free-items] "' + (cb.dataset.itemName || 'unnamed') + '" has no linked_product ' +
              '-> not added as a line item. Set linked_product (a $0 variant) on its ' +
              'free_items metaobject entry.'
            );
            return;
          }
          var freeProps = { _free_with: currentVariant.id.toString() };
          // Fulfilment note from the metaobject (e.g. "Ships in 6 weeks, separate
          // from bike."). Underscore-prefixed so it stays off the checkout page
          // while remaining visible on the order for whoever picks it.
          if (cb.dataset.itemNote) {
            freeProps['_note'] = cb.dataset.itemNote;
          }
          // Capture optional personalization name typed into this item's input
          var wrap = cb.closest('.product-add-ons-item-wrapper');
          var nameInput = wrap ? wrap.querySelector('.product-add-ons-name-input') : null;
          if (nameInput && nameInput.value.trim()) {
            freeProps['Name'] = nameInput.value.trim();
          }
          itemsToAdd.push({
            id: freeVariantId,
            quantity: 1,
            properties: freeProps
          });
          itemsMeta.push({
            source: 'free',
            name: cb.dataset.itemName || ('Free item #' + freeVariantId),
            variantId: freeVariantId
          });
        });

        var checkedUpgrades = this.section.querySelectorAll('.upgrade-item-checkbox:checked');
        checkedUpgrades.forEach((cb) => {
          var variantId = parseInt(cb.dataset.variantId, 10);
          if (!variantId) return;
          itemsToAdd.push({ id: variantId, quantity: 1 });
          itemsMeta.push({
            source: 'upgrade',
            name: cb.dataset.itemName || cb.dataset.upgradeName || ('Upgrade #' + variantId),
            variantId: variantId
          });
        });

        var personalizationItem = this.getPersonalizationItem();
        if (personalizationItem) {
          itemsToAdd.push(personalizationItem);
          itemsMeta.push({
            source: 'personalization',
            name: 'Personalization',
            variantId: personalizationItem.id
          });
        }

        // Add everything in one request so Shopify preserves the array order
        var addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: itemsToAdd })
        });
        if (!addResponse.ok) {
          var addError = await addResponse.json().catch(function() { return {}; });
          console.error('Cart add failed:', addResponse.status, addError);

          // Skip the per-item probe on rate limits / network errors — probing
          // would just amplify the rate limit. Only diagnose true 422s
          // (validation/availability failures).
          if (addResponse.status === 422) {
            // Diagnose: try each line item independently to see exactly which
            // ones Shopify rejects. We do NOT keep them in the cart — clear
            // them after the probe. Adds a small delay between probes to
            // stay under Shopify's per-IP rate limit.
            var failures = [];
            var anyProbeRateLimited = false;
            for (var i = 0; i < itemsToAdd.length; i++) {
              if (i > 0) {
                await new Promise(function (r) { setTimeout(r, 350); });
              }
              var probe;
              try {
                probe = await fetch('/cart/add.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ items: [itemsToAdd[i]] })
                });
              } catch (e) {
                continue;
              }
              if (probe.status === 429) { anyProbeRateLimited = true; break; }
              if (!probe.ok) {
                var pErr = await probe.json().catch(function () { return {}; });
                failures.push({
                  source: itemsMeta[i].source,
                  name: itemsMeta[i].name,
                  variantId: itemsMeta[i].variantId,
                  reason: pErr.description || pErr.message || ('HTTP ' + probe.status)
                });
              }
            }
            // Clean up any probe items that DID succeed so the cart isn't dirty
            try { await fetch('/cart/clear.js', { method: 'POST' }); } catch (e) {}

            if (failures.length) {
              console.group('%cCart add — unavailable products', 'color:#c00;font-weight:bold');
              failures.forEach(function (f) {
                console.error('• [' + f.source + '] "' + f.name + '" (variant ' + f.variantId + ') — ' + f.reason);
              });
              console.groupEnd();
              throw new Error('Unavailable: ' + failures.map(function (f) { return f.name; }).join(', '));
            }

            if (anyProbeRateLimited) {
              console.warn('Shopify is rate-limiting cart requests. Please wait ~1 minute and try again.');
              throw new Error('Rate limited during diagnosis');
            }
          } else if (addResponse.status === 429) {
            console.warn('Shopify is rate-limiting cart requests. Please wait ~1 minute and try again.');
            throw new Error('Rate limited');
          }

          throw new Error(addError.description || addError.message || 'Failed to add to cart');
        }

        // Note: the cart drawer template renders main-product line items
        // first (see snippets/cart-drawer-juiced.liquid), so no client-side
        // reordering is required here.

        this.onAddToCartSuccess();
      } catch (error) {
        console.error('Add to cart error:', error);
        if (addToCartBtn) {
          addToCartBtn.textContent = 'Error - Try Again';
          setTimeout(() => {
            addToCartBtn.disabled = false;
            addToCartBtn.textContent = addToCartBtn.dataset.addText || 'Add to Cart';
          }, 2000);
        }
      }
    });
  }

  async onAddToCartSuccess() {
    var btn = this.section.querySelector('.product-add-to-cart');
    if (btn) {
      btn.textContent = 'Added!';
      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = btn.dataset.addText || 'Add to Cart';
      }, 1500);
    }

    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:open'));
    await this.refreshCartDrawer();

    var cartDrawer = document.getElementById('CartDrawer');
    var cartOverlay = document.getElementById('CartDrawerOverlay');
    if (cartDrawer) { cartDrawer.classList.add('active'); cartDrawer.setAttribute('aria-hidden', 'false'); }
    if (cartOverlay) cartOverlay.classList.add('active');

    this.updateCartCount();
  }

  async refreshCartDrawer() {
    try {
      var response = await fetch(window.location.pathname + '?sections=cart-drawer-juiced');
      if (!response.ok) throw new Error('Failed to fetch cart drawer');
      var data = await response.json();
      var html = data['cart-drawer-juiced'];
      if (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newItems = doc.getElementById('CartItems');
        var currentItems = document.getElementById('CartItems');
        if (newItems && currentItems) currentItems.innerHTML = newItems.innerHTML;

        var newTotal = doc.getElementById('CartTotal');
        var currentTotal = document.getElementById('CartTotal');
        if (newTotal && currentTotal) currentTotal.textContent = newTotal.textContent;

        var newFooter = doc.querySelector('.cart-drawer-footer');
        var currentFooter = document.querySelector('.cart-drawer-footer');
        if (newFooter && currentFooter) currentFooter.className = newFooter.className;

        this.attachCartDrawerListeners();
      }
    } catch (error) {
      console.error('Failed to refresh cart drawer:', error);
    }
  }

  attachCartDrawerListeners() {
    var cartDrawer = document.getElementById('CartDrawer');
    if (!cartDrawer) return;
    var self = this;
    cartDrawer.querySelectorAll('[data-action="decrease-quantity"]').forEach(btn => {
      btn.addEventListener('click', (e) => self.handleQuantityChange(e, -1));
    });
    cartDrawer.querySelectorAll('[data-action="increase-quantity"]').forEach(btn => {
      btn.addEventListener('click', (e) => self.handleQuantityChange(e, 1));
    });
    cartDrawer.querySelectorAll('[data-action="remove-item"]').forEach(btn => {
      btn.addEventListener('click', (e) => self.handleRemoveItem(e));
    });
  }

  async handleQuantityChange(e, delta) {
    var line = parseInt(e.currentTarget.dataset.line);
    var input = document.querySelector('[data-quantity-input][data-line="' + line + '"]');
    if (!input) return;
    await this.updateCartLine(line, Math.max(0, (parseInt(input.value) || 1) + delta));
  }

  async handleRemoveItem(e) {
    await this.updateCartLine(parseInt(e.currentTarget.dataset.line), 0);
  }

  async updateCartLine(line, quantity) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      await this.refreshCartDrawer();
      await this.updateCartCount();
    } catch (error) {
      console.error('Failed to update cart line:', error);
    }
  }

  async updateCartCount() {
    try {
      var response = await fetch('/cart.js');
      var cart = await response.json();
      document.querySelectorAll('[data-cart-count], .cart-count, .cart-count-bubble').forEach(el => {
        el.textContent = cart.item_count;
        if (cart.item_count > 0) el.classList.remove('hidden');
      });
    } catch (error) {}
  }

  destroy() {
    if (this.splide) this.splide.destroy();
  }
}

// Init
function initPDPDetailV2Sections() {
  var sections = document.querySelectorAll('[data-pdp-v2]');
  sections.forEach(function(section) {
    if (section.dataset.pdpV2Init) return;
    section.dataset.pdpV2Init = '1';
    new PDPDetailV2(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPDPDetailV2Sections);
} else {
  initPDPDetailV2Sections();
}

document.addEventListener('shopify:section:load', function(event) {
  var section = event.target;
  if (section && section.querySelector('[data-pdp-v2]')) {
    new PDPDetailV2(section);
  }
});
